import { type ZodObject, type ZodRawShape, z } from "zod";
// Deep-path import (vs `../internal/index.js`) is deliberate — `_registry` is
// a side-effect-free leaf, and going through the internal barrel would pull
// tracing.ts → config.ts in at type-schema load time and crash on TDZ when a
// test imports a public schema before config is initialized.
import { _mark_sensitive, _registry } from "../internal/sensitive.js";

/**
 * @packageDocumentation
 * @module act/types
 * @category Types
 * Zod schemas and helpers for the Act Framework.
 */

/**
 * An empty Zod schema (no properties).
 */
export const ZodEmpty = z.record(z.string(), z.never());

/**
 * Sensitive-data foundation re-exports (#855 / epic #566).
 *
 * - `REDACTED` / `SHREDDED` — sentinels placed in `event.data[field]`
 *   when the caller isn't authorized to see a sensitive field
 *   (`.discloses(predicate)` returned `false` or none was declared —
 *   recoverable) or the underlying PII was wiped (`Store.forget_pii` —
 *   irrecoverable).
 *   top-level field names marked via {@link sensitive}. A pure,
 *   read-only helper that inspects the out-of-band sensitive registry (a
 *   process-global `WeakMap`) `sensitive()` populates, otherwise
 *   unreachable from outside the package. Surfaced for adapters and
 *   tooling that must reflect input sensitivity on their own wire
 *   surface — e.g. the `@rotorsoft/act-http/openapi` emitter marks these
 *   fields `writeOnly` + `format: password` so generated clients and
 *   Swagger UI don't echo PII freely. Returns `[]` for non-object
 *   schemas or objects with no sensitive fields; top-level shape only.
 */
export { pii_fields, REDACTED, SHREDDED } from "../internal/sensitive.js";

/**
 * Mark a Zod schema as sensitive. Returns the same schema instance — the
 * marker is registered out-of-band so the static type is preserved and the
 * call site reads as a pure annotation.
 *
 * Idempotent: re-wrapping an already-sensitive schema is a no-op.
 *
 * The marker is what the orchestrator inspects to split event payloads into
 * `data` + `pii` on commit, gate reads via `.discloses`, and strip handler
 * payloads. Part of the sensitive-data foundation (#855 / epic #566).
 *
 * @example
 * ```ts
 * import { z } from "zod";
 * import { state, sensitive } from "@rotorsoft/act";
 *
 * const UserRegistered = z.object({
 *   email: sensitive(z.string()),
 *   name: sensitive(z.string()),
 *   plan: z.enum(["free", "pro"]),  // not sensitive — stays in events.data
 * });
 * ```
 *
 * @param schema - The Zod schema to mark sensitive.
 * @returns The same schema instance, unmodified at the type level.
 */
export function sensitive<T extends z.ZodType>(schema: T): T {
  _registry.add(schema, { sensitive: true });
  // Also stamp the def, so the marker survives the clone Zod produces for
  // any refinement chained AFTER this call — `sensitive(z.string()).min(1)`
  // used to lose it silently and write plaintext into `events.data` (#1417).
  _mark_sensitive(schema);
  return schema;
}

/**
 * Zod schema for an actor (user, system, etc.).
 */
export const ActorSchema = z
  .object({
    id: z.string(),
    name: z.string(),
  })
  .loose()
  .readonly();

/**
 * Zod schema for a target (stream and actor info).
 */
export const TargetSchema = z
  .object({
    stream: z.string(),
    actor: ActorSchema,
    expectedVersion: z.number().optional(),
  })
  .loose()
  .readonly();

/**
 * Zod schema for causation event metadata.
 */
export const CausationEventSchema = z.object({
  id: z.number(),
  name: z.string(),
  stream: z.string(),
});

/**
 * Zod schema for event metadata (correlation and causation).
 */
export const EventMetaSchema = z
  .object({
    correlation: z.string(),
    causation: z.object({
      action: TargetSchema.and(z.object({ name: z.string() })).optional(),
      event: CausationEventSchema.optional(),
    }),
  })
  .readonly();

/**
 * Zod schema for committed event metadata (id, stream, version, created, meta).
 */
export const CommittedMetaSchema = z
  .object({
    id: z.number(),
    stream: z.string(),
    version: z.number(),
    created: z.date(),
    meta: EventMetaSchema,
  })
  .readonly();

/**
 * Type representing the full state schema for a domain.
 * @property events - Map of event names to Zod schemas.
 * @property actions - Map of action names to Zod schemas.
 * @property state - Zod schema for the state object.
 */
export type StateSchema = Readonly<{
  events: Record<string, ZodObject<ZodRawShape> | typeof ZodEmpty>;
  actions: Record<string, ZodObject<ZodRawShape> | typeof ZodEmpty>;
  state: ZodObject<ZodRawShape>;
}>;

/**
 * Query options for event store queries.
 */
export const QuerySchema = z
  .object({
    stream: z.string().optional(),
    names: z.string().array().optional(),
    before: z.number().optional(),
    after: z.number().optional(),
    limit: z.number().optional(),
    created_before: z.date().optional(),
    created_after: z.date().optional(),
    backward: z.boolean().optional(),
    correlation: z.string().optional(),
    with_snaps: z.boolean().optional(),
    stream_exact: z.boolean().optional(),
  })
  .readonly();
